export type FeatureRequestStatus =
  | 'under_review'
  | 'planned'
  | 'in_progress'
  | 'shipped'
  | 'not_planned'
  | 'custom_dev';

// Mirrors the backend's feature-request-categories.constants.ts.
export const FEATURE_REQUEST_CATEGORIES = [
  'Bookings & Scheduling',
  'Payments & Packs',
  'Member Experience',
  'Merchandise',
  'Messaging & Notifications',
  'Reporting & Analytics',
  'Partner & Integrations',
  'Other',
] as const;

export type FeatureRequestCategory = (typeof FEATURE_REQUEST_CATEGORIES)[number];

// Friendly labels — mirrors the backend's FEATURE_REQUEST_STATUS_LABELS.
export const STATUS_LABELS: Record<FeatureRequestStatus, string> = {
  under_review: 'Under review',
  planned: 'Planned',
  in_progress: 'In progress',
  shipped: 'Shipped',
  not_planned: 'Not planned',
  custom_dev: 'Custom development',
};

export interface StudioRef {
  _id: string;
  studioName?: string;
}

export interface StatusHistoryEntry {
  status: FeatureRequestStatus;
  changedAt: string;
  changedBy: string | null;
}

export interface FeatureRequestComment {
  _id: string;
  authorStudioId: StudioRef | null;
  isAdmin: boolean;
  body: string;
  createdAt: string;
}

export interface FeatureRequestListItem {
  _id: string;
  title: string;
  description: string;
  whyNeeded: string;
  category: FeatureRequestCategory | string;
  status: FeatureRequestStatus;
  visibility: 'public' | 'private';
  submittedByStudio: StudioRef;
  voteCount: number;
  mergedInto: string | null;
  shippedAt: string | null;
  createdAt: string;
  updatedAt: string;
  hasVoted: boolean;
}

export interface FeatureRequestDetail extends Omit<FeatureRequestListItem, 'submittedByStudio'> {
  submittedByStudio: string;
  statusHistory: StatusHistoryEntry[];
  comments: FeatureRequestComment[];
  alsoRequestedByCount: number;
}

export interface SimilarFeatureRequest {
  id: string;
  title: string;
  category: string;
  status: FeatureRequestStatus;
  voteCount: number;
  similarityScore: number;
}

export interface FeatureRequestListResponse {
  items: FeatureRequestListItem[];
  page: number;
  pageSize: number;
  total: number;
}

// ── Admin ──

export interface AdminFeatureRequest extends FeatureRequestListItem {
  adminNotes: string | null;
  effortEstimate: number | null;
}

export interface AdminFeatureRequestListResponse {
  items: AdminFeatureRequest[];
  page: number;
  pageSize: number;
  total: number;
}

export interface DuplicateCandidateRequest {
  _id: string;
  title: string;
  description: string;
  category: string;
  status: FeatureRequestStatus;
  voteCount: number;
  submittedByStudio: StudioRef;
}

export interface DuplicateCandidate {
  _id: string;
  requestA: DuplicateCandidateRequest;
  requestB: DuplicateCandidateRequest;
  similarityScore: number;
  status: 'pending' | 'merged' | 'dismissed';
  createdAt: string;
}

export interface ImpactRow {
  _id: string;
  title: string;
  voteCount: number;
  effortEstimate: number | null;
}

export interface PublicChangelogEntry {
  _id: string;
  requestId: { _id: string; title: string; category: string } | null;
  releaseNote: string;
  publishedAt: string;
  public: boolean;
}
